import { visit } from 'unist-util-visit';
import { CALLOUT_TYPES, CALLOUT_MARKER } from './calloutTypes';

/**
 * Preprocess markdown to normalize math syntax for remarkMath/KaTeX
 * and ensure proper header formatting.
 * Converts:
 * - \(...\) → $...$ (inline math)
 * - <div class="math-block">...</div> → $$...$$ (display math)
 * - Ensures ### headers have proper newlines
 */
export function preprocessMarkdown(raw = '') {
  if (!raw) return '';

  let processed = raw;

  // Convert \(...\) to $...$ for inline math
  processed = processed.replace(/\\\((.+?)\\\)/g, '$$$1$$');

  // Convert <div class="math-block">...</div> to $$...$$ for display math.
  // Uses a function replacer (not a '$$...$$' pattern string) because in a
  // String.replace pattern string, "$$" itself means "literal $" — writing
  // it directly here previously collapsed to single $ (inline math) instead
  // of the intended $$ (display math), so math-block content silently lost
  // its block/panel treatment.
  processed = processed.replace(
    /<div\s+class=["']math-block["']>\s*([\s\S]*?)\s*<\/div>/gi,
    (match, inner) => `\n$$\n${inner}\n$$\n`
  );

  // Convert <span class="math-inline">...</span> to $...$
  processed = processed.replace(
    /<span\s+class=["']math-inline["']>\s*([\s\S]*?)\s*<\/span>/gi,
    '$$$1$$'
  );

  // Ensure headers have a blank line before them (required for proper parsing)
  // This handles cases where headers follow immediately after other content
  processed = processed.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');

  return processed;
}

/**
 * Remark (mdast) plugin: turns a `> [!TYPE]` blockquote into a styled
 * callout `<div>` with a tracked eyebrow label. See calloutTypes.js for the
 * convention this implements; htmlCallouts.js is the hast-level equivalent
 * used by the raw-HTML lesson path.
 */
export function remarkCallouts() {
  return (tree) => {
    visit(tree, 'blockquote', (node) => {
      const firstParagraph = node.children?.[0];
      if (!firstParagraph || firstParagraph.type !== 'paragraph') return;
      const firstText = firstParagraph.children?.[0];
      if (!firstText || firstText.type !== 'text') return;

      const match = CALLOUT_MARKER.exec(firstText.value);
      if (!match) return;
      const meta = CALLOUT_TYPES[match[1]];
      if (!meta) return;

      // Strip the marker token from the rendered text.
      firstText.value = firstText.value.slice(match[0].length);
      if (firstText.value === '') {
        firstParagraph.children.shift();
      }

      node.data = {
        hName: 'div',
        hProperties: { className: ['callout', meta.className] },
      };
      node.children.unshift({
        type: 'paragraph',
        data: { hName: 'p', hProperties: { className: ['callout-label'] } },
        children: [{ type: 'text', value: meta.label }],
      });
    });
  };
}
